<template>
    <div class="form-input" :class="[
                                sizeableString ? 'form-input-' + sizeableString : '', {
                'is-active': isActive,
                'is-required': isRequired,
                'is-empty': isEmpty,
                'has-error': hasError,
                'has-warning': !hasError && props.warning,
                'is-inverted': props.inverted,
                'is-plain': props.plain,
                'is-disabled': props.disabled,
                'has-suffix': hasSuffix,
                'has-prefix': hasPrefix,
            }]">
        <div class="form-input__content">
            <div v-if="hasPrefix" class="form-input__prefix">
                <lazy-icon v-if="props.icon" :icon="props.icon"/>
            </div>
            <label class="form-input__field-wrapper">
                <span v-if="props.label" class="form-input__label">{{ props.label }}</span>

                <span v-if="props.prefix && (inputValue || inputValue === 0)" class="ml-8 form-input__field-content">{{ props.prefix }}</span>
                <input class="form-input__field" :class="props.inputClass" ref="inputElement" :enterkeyhint="props.enterkeyhint ? props.enterkeyhint : 'next'"
                       :type="props.decimals || !props.noSeparators ? 'tel' : 'number'" :autofocus="props.autofocus" :name="props.name"
                       :step="props.steps"
                       :max="props.rules ? props.rules.max : null"
                       :min="props.rules ? props.rules.min : null"
                       v-model="inputValue" :placeholder="props.placeholder" :disabled="props.disabled" @keydown="handleKeydown" @blur="handleBlur" @focus="handleFocus" @keypress="isAllowedInput($event)"/>
                <span v-if="props.suffix && (inputValue || inputValue === 0)" class="absolute left-0 ml-8 form-input__field-content"><span class="opacity-0">{{ inputValue }}</span> {{ props.suffix }}</span>
            </label>
            <div v-if="hasSuffix" class="form-input__suffix">
				<slot name="suffix" />
                <lazy-icon v-if="!props.disabled && props.clearable && submitValue" :icon="icons.reset" class="cursor-pointer" :class="{'mr-4': !!props.help}" @click="clearValue"/>
                <lazy-tooltip v-if="props.help" :text="props.help" left with-touch>
                    <lazy-icon :icon="icons.help"/>
                </lazy-tooltip>
            </div>
        </div>
        <div v-if="!props.hideDetails" class="form-input__details">
            <span v-if="hasError && errorMessage" class="form-input__message" tabindex="-1">{{ errorMessage }}</span>
            <span v-else-if="props.warning" class="form-input__message" tabindex="-1">{{ props.warning }}</span>
            <span v-else-if="props.info" class="form-input__message" tabindex="-1">{{ props.info }}</span>
        </div>
    </div>
</template>

<script setup lang="ts">
import {padNumber} from "@spoferan/spoferan-ts-core";
import {helpIcon, resetIcon} from "../../../config";
import {TextInputComponentEmits, type TextInputComponentProps, useTextInputComponent} from "../../../composables/useTextInputComponent";
import {ref, watch, computed} from "vue";
import type {Ref} from "vue";

const props = withDefaults(defineProps<{
	// The steps on how to increase or decrease the value
	steps?: number,

	// States if decimal values are allowed and displayed on the input
	decimals?: boolean,

	// States if a thousand separators shall be hidden
	noSeparators?: boolean,

	// Whether negative values are allowed
	allowNegative?: boolean,

	// States if the submit value shall be transformed to a full integer
	// which multiplies the displayed value by 100 to represent decimals
	integer?: boolean,

	// The prefix symbol of the input
	prefix?: string,

	// The suffix symbol of the input
	suffix?: string,
} & TextInputComponentProps>(), {
	type: 'number',
	prefix: '',
	suffix: '',
});

const emit = defineEmits(TextInputComponentEmits);

const icons = {
	reset: resetIcon,
	help: helpIcon,
};

const separators = {
	decimal: ',',
	thousands: props.noSeparators ? '' : '.',
}

const inputElement: Ref<HTMLElement | null> = ref(null);

const {
	submitValue,
	sizeableString,
	isActive,
	isRequired,
	hasError,
	hasPrefix,
	hasSuffix,
	errorMessage,
	handleKeydown,
	handleBlur,
	handleFocus,
	clearValue
} = useTextInputComponent(inputElement, props, emit, {
	onDeleteKey: handleDeleteKey
});
watch(submitValue, (newValue) => {
	inputValue.value = parseFromIntegerIfRequired(newValue)
});

const inputValue = ref(parseFromIntegerIfRequired(submitValue.value));
watch(inputValue, (newValue) => {
	submitValue.value = parseToIntegerIfRequired(newValue)
});

// Whether the user currently wants to input a negative value
const wantsNegative = ref(props.modelValue && props.modelValue < 0);
watch(wantsNegative, () => {
	// Trigger auto converter
	submitValue.value = parseToIntegerIfRequired(inputValue.value)
});

const precision = computed(() => {
	return props.decimals ? 2 : 0
});

const isEmpty = computed(() => {
	return !isDefined(inputValue.value) && !isDefined(props.placeholder);
});

function isAllowedInput(event) {
	const commaKeyCode = 44;
	const minusKeyCode = 45;
	const plusKeyCode = 43;

	// Only allow the input of numbers
	event = (event) ? event : window.event;
	const charCode = (event.which) ? event.which : event.keyCode;

	// When the user inputs a minus, we assume he wants to input a negative number
	if (props.allowNegative) {
		if (charCode === minusKeyCode) {
			wantsNegative.value = true;
		} else if (charCode === plusKeyCode) {
			wantsNegative.value = false;
		}
	}

	if ((charCode > 31 && (charCode < 48 || charCode > 57)) && (!props.decimals || (charCode !== commaKeyCode))) {
		event.preventDefault();
	} else {
		return true;
	}
}

function handleDeleteKey() {

	// Unset the negative sign when the user tries to delete it
	if (wantsNegative.value && (!inputValue.value)) {
		wantsNegative.value = false;
	}
}

function parseFromIntegerIfRequired(value) {
	if (!value) {
		return value;
	}

	let floatValue = parseFloat(props.integer ? (value / 100) : value).toString();

	if (props.decimals) {
		floatValue = floatValue.replace('.', separators.decimal);
	}

	return props.noSeparators ? floatValue : addThousandSeparator(floatValue, separators.thousands);
}

function parseToIntegerIfRequired(value) {
	if (!value) {
		return value;
	}

	if ((wantsNegative.value && value > 0) || (!wantsNegative.value && value < 0)) {
		value = -value;
	}

	value = value.toString();

	// Remove the thousands' separator if one exists
	value = value.replace(separators.thousands, '');

	let splitValue = value.split(separators.decimal);
	let intValue = splitValue[0].toString();
	let decimalValue = splitValue.length > 1 ? padNumber(splitValue[1], precision.value, true) : '00';

	let concatIntValue = parseInt(intValue + decimalValue)

	return props.integer ? concatIntValue : (concatIntValue / 100).toFixed(precision.value);
}

function addThousandSeparator(integer, separator) {
	return integer.replace(/(\d)(?=(?:\d{3})+\b)/gm, `$1${separator}`)
}

function isDefined(value) {
	return [null, undefined].indexOf(value) < 0;
}
</script>